import React, { useState, useEffect, useCallback } from 'react';
import { HorseRead, UserHorseCollectionRead, HorseStatus, UserHorseNoteRead, ProfileWithHorseCount, ProfileRead, HorsePricingRead, HorsePropertiesRead, Country } from '../types/api';
import { apiService } from '../services/api';
import { ProfileSelectionPopup } from './ProfileSelectionPopup';
import { ChevronDown, ChevronUp, Link2, Trash2, Plus, Eye, XCircle, Edit2, Check, Bell, BellOff } from 'lucide-react';
// import { HorseStatusSelector } from './HorseStatusSelector';
import { HorseNotes } from './HorseNotes';
// import { HorseOwnersHistory } from './HorseOwnersHistory';
import { urlParams } from '../utils/urlParams';
import { LinkPrivateHorseModal } from './PrivateHorseModals';
import { HorseContactInfo } from './HorseContactInfo';
import { HorseTraits } from './HorseTraits';
// import { CompetitionChart } from './CompetitionChart';
// import { CompetitionResultsList } from './CompetitionResultsList';
import { HorsePriceDisplay } from './HorsePriceDisplay';
import { getHorseAvatarUrl } from '../utils/horseAvatar';
// Removed duplicate icon import
import { HorsePerformanceSection } from './HorsePerformanceSection';
import { getFlagUrlByNameOrCode } from '../utils/countryFlag';
import { formatPersonName } from '../utils/personName';
import { formatHorseName } from '../utils/horseName';

interface HorseDetailViewProps {
  horseId: string;
  onNavigateBack?: () => void;
  onTitleChange?: (title: string | null) => void;
}

export const HorseDetailView: React.FC<HorseDetailViewProps> = ({ 
  horseId, 
  onNavigateBack,
  onTitleChange
}) => {
  const [horse, setHorse] = useState<HorseRead | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [inCollection, setInCollection] = useState(false);
  const [currentCollectionData, setCurrentCollectionData] = useState<UserHorseCollectionRead | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [hasPrivateData, setHasPrivateData] = useState(false);
  const [privateDataCheckDone, setPrivateDataCheckDone] = useState(false); // True once initial check is complete
  const [forceShowPrivateSection, setForceShowPrivateSection] = useState(false);
  const [privateExpanded, setPrivateExpanded] = useState(true);
  const [isEditingPrivate, setIsEditingPrivate] = useState(false);
  const [showNotesForm, setShowNotesForm] = useState(false);
  const [preloadedNotes, setPreloadedNotes] = useState<UserHorseNoteRead[] | undefined>(undefined);
  const [preloadedPricing, setPreloadedPricing] = useState<HorsePricingRead | null | undefined>(undefined);
  const [preloadedProperties, setPreloadedProperties] = useState<HorsePropertiesRead | null | undefined>(undefined);
  const [preloadedContactNotes, setPreloadedContactNotes] = useState<UserHorseNoteRead[] | undefined>(undefined);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [maxShowHeight, setMaxShowHeight] = useState<number | null>(null);
  const [countries, setCountries] = useState<Country[]>([]);
  const isPrivateHorse = currentCollectionData?.is_private ?? false;

  // Profile popup state
  const [profiles, setProfiles] = useState<ProfileWithHorseCount[]>([]);
  const [profileAssignmentPending, setProfileAssignmentPending] = useState<{
    horseId: string;
    collectionItem: UserHorseCollectionRead;
  } | null>(null);

  const refreshCollectionEntry = useCallback(async (): Promise<UserHorseCollectionRead | null> => {
    if (!apiService.isAuthenticated()) {
      setInCollection(false);
      setCurrentCollectionData(null);
      return null;
    }

    try {
      const collection = await apiService.getCollection();
      const entry = collection.find(item => item.horse_id === horseId) || null;
      if (entry) {
        setInCollection(true);
        setCurrentCollectionData(entry);
      } else {
        setInCollection(false);
        setCurrentCollectionData(null);
      }
      return entry;
    } catch (collectionError) {
      console.warn('Failed to load collection entry:', collectionError);
      return null;
    }
  }, [horseId]);

  const loadHorseDetail = useCallback(async () => {
    onTitleChange?.(null);
    // Reset state for new horse - do all resets together
    setHasPrivateData(false);
    setPrivateDataCheckDone(false);
    setForceShowPrivateSection(false);
    setLoading(true);
    setError(null);
    
    try {
      const collectionEntry = await refreshCollectionEntry();

      let loadedHorse: HorseRead | null = null;
      
      if (collectionEntry?.is_private && collectionEntry.horse) {
        loadedHorse = collectionEntry.horse;
      } else {
        loadedHorse = await apiService.getHorseDetails(horseId);
      }
      
      // Check private data once after horse is loaded, if in collection
      let foundPrivateData = false;
      
      if (loadedHorse && collectionEntry && apiService.isAuthenticated()) {
        try {
          const [contactNotes, pricing, properties, mediaLinks, textNotes] = await Promise.all([
            apiService.getHorseNotes(loadedHorse.unified_horse_key, 'contact').catch(() => []),
            apiService.getHorsePricing(loadedHorse.unified_horse_key).catch(() => null),
            apiService.getHorseProperties(loadedHorse.unified_horse_key).catch(() => null),
            apiService.getLinkedMedia(loadedHorse.unified_horse_key, 0, 1).catch(() => []),
            apiService.getHorseNotes(loadedHorse.unified_horse_key, 'text').catch(() => [])
          ]);

          const hasContact = contactNotes.length > 0 && contactNotes.some(n => n.is_active);
          const hasPricing = pricing !== null;
          const hasProperties = properties !== null;
          const hasMedia = mediaLinks.length > 0;
          const hasNotes = textNotes.length > 0 && textNotes.some(n => n.is_active);

          foundPrivateData = hasContact || hasPricing || hasProperties || hasMedia || hasNotes;
          setPreloadedNotes(textNotes);
          setPreloadedPricing(pricing);
          setPreloadedProperties(properties);
          setPreloadedContactNotes(contactNotes);
        } catch (err) {
          console.warn('Failed to check private data:', err);
        }
      }
      
      // Set all final states together in one batch
      setHorse(loadedHorse);
      onTitleChange?.(
        loadedHorse
          ? formatHorseName(loadedHorse.unified_horse_name) || (loadedHorse as any).name || null
          : null
      );
      setHasPrivateData(foundPrivateData);
      setPrivateDataCheckDone(true);
    } catch (error) {
      console.error('Error loading horse details:', error);
      setError(error instanceof Error ? error.message : 'Failed to load horse details');
      setPrivateDataCheckDone(true); // Mark as done even on error to avoid stuck loading
      onTitleChange?.(null);
    } finally {
      setLoading(false);
    }
  }, [horseId, onTitleChange, refreshCollectionEntry]);

  useEffect(() => {
    loadHorseDetail();
  }, [loadHorseDetail]);

  useEffect(() => {
    return () => {
      onTitleChange?.(null);
    };
  }, [onTitleChange]);

  // Load profiles for the assignment popup
  useEffect(() => {
    if (!apiService.isAuthenticated()) return;
    apiService.getProfiles().then(setProfiles).catch(() => {});
  }, []);

  // Load countries for flag display
  useEffect(() => {
    apiService.getCountries().then(setCountries).catch(() => {});
  }, []);

  // Re-check private data after user actions (add/edit/delete) - does NOT show loading state
  const recheckPrivateData = useCallback(async () => {
    const hId = horse?.unified_horse_key;
    if (!hId || !apiService.isAuthenticated()) {
      return;
    }

    try {
      const [contactNotes, pricing, properties, mediaLinks, notes] = await Promise.all([
        apiService.getHorseNotes(hId, 'contact').catch(() => []),
        apiService.getHorsePricing(hId).catch(() => null),
        apiService.getHorseProperties(hId).catch(() => null),
        apiService.getLinkedMedia(hId, 0, 1).catch(() => []),
        apiService.getHorseNotes(hId).catch(() => [])
      ]);

      const hasContact = contactNotes.length > 0 && contactNotes.some(n => n.is_active);
      const hasPricing = pricing !== null;
      const hasProperties = properties !== null;
      const hasMedia = mediaLinks.length > 0;
      const hasNotes = notes.length > 0 && notes.some(n => n.is_active);

      const hasAnyData = hasContact || hasPricing || hasProperties || hasMedia || hasNotes;
      setHasPrivateData(hasAnyData);
    } catch (error) {
      console.warn('Failed to recheck private data:', error);
      // On error, don't change the state - keep whatever we had
    }
  }, [horse?.unified_horse_key]);

  // Heart toggle removed from UI per new design; keep handler unused for now

  const handleDeletePrivateHorse = async () => {
    if (!isPrivateHorse) {
      return;
    }
    const confirmed = window.confirm('Are you sure you want to delete this private horse? This cannot be undone.');
    if (!confirmed) {
      return;
    }
    setIsLoading(true);
    try {
      await apiService.deletePrivateHorse(horseId);
      setInCollection(false);
      setCurrentCollectionData(null);
      window.dispatchEvent(new CustomEvent('collection:refresh'));
      urlParams.navigateBack();
    } catch (error) {
      console.error('Error deleting private horse:', error);
      alert('Failed to delete private horse');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLinkSuccess = (entry: UserHorseCollectionRead, targetHorseId: string) => {
    setCurrentCollectionData(entry);
    setInCollection(true);
    setShowLinkModal(false);
    window.dispatchEvent(new CustomEvent('collection:refresh'));
    alert('Private horse linked successfully.');
    urlParams.navigateToHorseDetail(targetHorseId);
  };

  const ensureInCollection = async () => {
    if (!apiService.isAuthenticated() || !horse) {
      alert('Please log in to manage status');
      return null;
    }
    if (inCollection && currentCollectionData) {
      return currentCollectionData;
    }
    try {
      const result = await apiService.addToCollection(horse.unified_horse_key);
      setInCollection(true);
      setCurrentCollectionData(result);
      return result;
    } catch (error) {
      console.error('Failed to add to collection:', error);
      setStatusError('Failed to add to collection');
      return null;
    }
  };

  const handleRemoveFromCollection = async () => {
    if (!horse) return;
    const prev = currentCollectionData;
    setIsLoading(true);
    setStatusError(null);
    try {
      await apiService.removeFromCollection(horse.unified_horse_key);
      setInCollection(false);
      setCurrentCollectionData(null);
      window.dispatchEvent(new CustomEvent('collection:refresh'));
    } catch (error) {
      console.error('Error removing from collection:', error);
      setStatusError('Failed to remove from collection. Please try again.');
      setCurrentCollectionData(prev);
    } finally {
      setIsLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: HorseStatus | null) => {
    if (!horse) return;

    if (newStatus === null) {
      await handleRemoveFromCollection();
      return;
    }

    const existing = currentCollectionData || (await ensureInCollection());
    if (!existing) return;

    const prev = { ...existing };
    setStatusError(null);
    setCurrentCollectionData({ ...existing, status: newStatus });
    setIsLoading(true);
    try {
      const result = await apiService.updateCollectionStatus(horse.unified_horse_key, newStatus);
      setCurrentCollectionData(result);

      // Offer profile assignment after Watch
      if (newStatus === 'watch') {
        setProfileAssignmentPending({ horseId: horse.unified_horse_key, collectionItem: result });
      }
    } catch (error) {
      console.error('Error updating status:', error);
      setStatusError('Failed to update status. Please try again.');
      setCurrentCollectionData(prev);
    } finally {
      setIsLoading(false);
    }
  };

  const handleNotificationToggle = async () => {
    if (!horse || !currentCollectionData) return;
    const prev = { ...currentCollectionData };
    const newValue = !currentCollectionData.notification_enabled;
    setCurrentCollectionData({ ...currentCollectionData, notification_enabled: newValue });
    try {
      const result = await apiService.updateCollectionStatus(horse.unified_horse_key, currentCollectionData.status, newValue);
      setCurrentCollectionData(result);
    } catch (error) {
      console.error('Error toggling notification:', error);
      setCurrentCollectionData(prev);
    }
  };

  const handleNavigateBack = () => {
    if (onNavigateBack) {
      onNavigateBack();
    } else {
      urlParams.navigateBack();
    }
  };

  const formatYear = (year?: number | null) => {
    return year ? year.toString() : 'Unknown';
  };

  const formatHeight = (height?: number | null) => {
    return height ? `${height} cm` : 'Unknown';
  };

  const formatShowHeight = (height?: number | null) => {
    return height ? `${Math.round(height)} cm` : '';
  };

  const formatPrizeTotals = () => {
    if (!horse) return null;
    const prizes: string[] = [];
    if (horse.total_prizes_eur) prizes.push(`€${horse.total_prizes_eur.toLocaleString()}`);
    if (horse.total_prizes_gbp) prizes.push(`£${horse.total_prizes_gbp.toLocaleString()}`);
    if (horse.total_prizes_usd) prizes.push(`$${horse.total_prizes_usd.toLocaleString()}`);
    if (horse.total_prizes_chf) prizes.push(`CHF ${horse.total_prizes_chf.toLocaleString()}`);
    if (horse.total_prizes_aud) prizes.push(`A$${horse.total_prizes_aud.toLocaleString()}`);
    if (horse.total_prizes_cad) prizes.push(`C$${horse.total_prizes_cad.toLocaleString()}`);
    return prizes.length > 0 ? prizes.join(' • ') : null;
  };

  const formatPedigree = () => {
    if (!horse) return null;
    const parts: string[] = [];
    if (horse.unified_sire_name) parts.push(formatHorseName(horse.unified_sire_name)!);
    if (horse.unified_dam_sire) parts.push(formatHorseName(horse.unified_dam_sire)!);
    return parts.length > 0 ? parts.join(' X ') : null;
  };

  const _getProfileInitial = () => {
    if (horse?.unified_horse_name) {
      return horse.unified_horse_name.charAt(0).toUpperCase();
    }
    return 'H';
  };

  // Status label/time removed from UI per new triage design

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Error Loading Horse</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={handleNavigateBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!horse) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Horse Not Found</h2>
          <p className="text-gray-600 mb-4">The requested horse could not be found.</p>
          <button
            onClick={handleNavigateBack}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Main Content */}
      <div className="max-w-[320px] lg:max-w-[500px] mx-auto px-6 pt-8 pb-52">

        {/* Top Section - Public Horse Profile */}
        <div className="mb-6">
          <div className="flex items-start gap-3 lg:gap-4 mb-4">
            {/* Profile Image */}
            <div className="flex-shrink-0 w-[80px] h-[76px] lg:w-[132px] lg:h-[126px] rounded-[80px] border-2 border-white shadow-[0px_4px_4px_0px_rgba(0,0,0,0.1)] bg-white overflow-visible">
              <img
                src={getHorseAvatarUrl(horse.unified_sex, horse.unified_color)}
                alt={horse.unified_horse_name ?? 'Horse avatar'}
                className="w-full h-full object-contain"
                loading="eager"
              />
            </div>
            
            {/* Horse Basic Info */}
            <div className="flex-1 min-w-0">
              <div className="text-left text-sm text-[#233454] leading-normal space-y-1">
                <p className="font-normal">
                  {[
                    formatYear(horse.unified_birth_year),
                    horse.unified_sex || null,
                    horse.unified_height_cm ? formatHeight(horse.unified_height_cm) : null,
                    horse.unified_color || null
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                </p>
                {maxShowHeight && (
                  <p className="font-normal">{`Level: ${formatShowHeight(maxShowHeight)}`}</p>
                )}
                {formatPrizeTotals() && (
                  <p className="font-normal">{`Prizes: ${formatPrizeTotals()}`}</p>
                )}
                <p className="font-normal">{formatPedigree() || ''}</p>
                {horse.current_rider_name && (
                  <p className="font-normal">{`Rider: ${formatPersonName(horse.current_rider_name)}`}</p>
                )}
                <p className="font-normal">
                  {[
                    horse.unified_fei_id || horse.unified_studbook_id || null,
                    horse.unified_studbook || null,
                  ]
                    .filter(Boolean)
                    .join(' • ')}
                </p>
                {horse.horse_country && (
                  <p className="font-normal inline-flex items-center gap-1">
                    {getFlagUrlByNameOrCode(horse.horse_country, countries) && (
                      <img
                        src={getFlagUrlByNameOrCode(horse.horse_country, countries)!}
                        alt={horse.horse_country}
                        className="w-4 h-4 rounded-full"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    {horse.horse_country}
                  </p>
                )}
                <p className="font-normal">{horse.unified_breeder ? `Breeder: ${horse.unified_breeder}` : ''}</p>
              </div>

              {/* Actions: Watch / Skip */}
              <div className="mt-4 flex items-center gap-2 lg:gap-3 max-w-md">
                <button
                  onClick={() =>
                    currentCollectionData?.status === 'watch'
                      ? handleStatusChange(null)
                      : handleStatusChange('watch')
                  }
                  disabled={isLoading}
                  className={`inline-flex items-center justify-center gap-1.5 lg:gap-2 px-3 lg:px-6 h-11 rounded-full text-sm font-semibold flex-1 min-w-0 overflow-hidden whitespace-nowrap transition-colors ${
                    currentCollectionData?.status === 'watch'
                      ? 'bg-[#132B64] hover:bg-[#0F244F] text-white border border-transparent shadow-[0_8px_22px_rgba(19,43,100,0.42)]'
                      : 'bg-white text-[#132B64] hover:bg-[#e6ecfa] border border-[#132B64] shadow-[0_4px_12px_rgba(19,43,100,0.18)]'
                  }`}
                  title="Add to your prospect list for this profile"
                >
                  <Eye
                    size={18}
                    className="shrink-0 lg:w-5 lg:h-5"
                    style={{ width: '18px', height: '18px' }}
                  />
                  {currentCollectionData?.status === 'watch' ? 'Watching' : 'Watch'}
                </button>
                <button
                  onClick={() =>
                    currentCollectionData?.status === 'skip'
                      ? handleStatusChange(null)
                      : handleStatusChange('skip')
                  }
                  disabled={isLoading}
                  className={`inline-flex items-center justify-center gap-1.5 lg:gap-2 px-3 lg:px-6 h-11 rounded-full text-sm font-semibold flex-1 min-w-0 overflow-hidden whitespace-nowrap transition-colors ${
                    currentCollectionData?.status === 'skip'
                      ? 'bg-[#132B64] hover:bg-[#0F244F] text-white border border-transparent shadow-[0_8px_22px_rgba(19,43,100,0.42)]'
                      : 'bg-white text-[#132B64] hover:bg-[#e6ecfa] border border-[#132B64] shadow-[0_4px_12px_rgba(19,43,100,0.18)]'
                  }`}
                  title="Exclude from recommendations"
                >
                  <XCircle
                    size={18}
                    className="shrink-0 lg:w-5 lg:h-5"
                    style={{ width: '18px', height: '18px' }}
                  />
                  Skip
                </button>
              </div>
              {statusError && (
                <div className="text-xs text-red-600 mt-1 px-1">{statusError}</div>
              )}

              {/* Profile Badges + Edit */}
              {currentCollectionData?.profiles && currentCollectionData.profiles.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {currentCollectionData.profiles.map(profile => (
                    <span
                      key={profile.id}
                      className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#132B64]/10 text-[#132B64] border border-[#132B64]/20"
                    >
                      {profile.name}
                    </span>
                  ))}
                  <button
                    onClick={() => setProfileAssignmentPending({ horseId: horse.unified_horse_key, collectionItem: currentCollectionData })}
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 transition-colors"
                    title="Edit profiles"
                  >
                    <Edit2 size={14} className="text-[#132B64]" />
                  </button>
                  <button
                    onClick={handleNotificationToggle}
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 transition-colors"
                    title={currentCollectionData.notification_enabled ? 'Notifications enabled' : 'Notifications disabled'}
                  >
                    {currentCollectionData.notification_enabled ? (
                      <Bell size={14} className="text-[#132B64]" />
                    ) : (
                      <BellOff size={14} className="text-[#132B64]/40" />
                    )}
                  </button>
                </div>
              )}
              {inCollection && (!currentCollectionData?.profiles || currentCollectionData.profiles.length === 0) && (
                <div className="mt-3 flex items-center gap-1.5">
                  <button
                    onClick={() => setProfileAssignmentPending({ horseId: horse.unified_horse_key, collectionItem: currentCollectionData! })}
                    className="inline-flex items-center gap-1.5 text-xs text-[#132B64] hover:underline"
                    title="Assign to profiles"
                  >
                    <Plus size={14} />
                    Add to profile
                  </button>
                  <button
                    onClick={handleNotificationToggle}
                    className="inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-gray-100 transition-colors"
                    title={currentCollectionData!.notification_enabled ? 'Notifications enabled' : 'Notifications disabled'}
                  >
                    {currentCollectionData!.notification_enabled ? (
                      <Bell size={14} className="text-[#132B64]" />
                    ) : (
                      <BellOff size={14} className="text-[#132B64]/40" />
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Private Horse Actions */}
          {isPrivateHorse && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700">
                Private Horse
              </span>
              <button
                onClick={() => setShowLinkModal(true)}
                className="inline-flex items-center px-3 py-1.5 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
              >
                <Link2 size={16} className="mr-2" />
                Link to Public Horse
              </button>
              <button
                onClick={handleDeletePrivateHorse}
                className="inline-flex items-center px-3 py-1.5 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md hover:bg-red-100"
                disabled={isLoading}
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </button>
            </div>
          )}

        </div>

        {/* Middle Section - Private Scouting Notes (Green Box) */}
        {apiService.isAuthenticated() && (
          <div className="bg-[#cbe0be] rounded-[12px] mb-6 relative">
            {!privateDataCheckDone ? (
              /* Loading State */
              <div className="flex items-center justify-center py-8 px-4">
                <p className="text-sm text-[#233454]">Loading...</p>
              </div>
            ) : !hasPrivateData && !forceShowPrivateSection ? (
              /* Collapsed (no data) - "Add private info" button */
              <button
                className="h-[41px] w-full flex items-center justify-center gap-6 hover:opacity-80 transition-opacity"
                onClick={async () => {
                  // Ensure horse is in collection before allowing private data edits
                  if (!inCollection) {
                    const added = await ensureInCollection();
                    if (!added) {
                      return; // Failed to add to collection
                    }
                    // Set watch status and show profile selector
                    try {
                      const result = await apiService.updateCollectionStatus(horse.unified_horse_key, 'watch');
                      setCurrentCollectionData(result);
                      setProfileAssignmentPending({ horseId: horse.unified_horse_key, collectionItem: result });
                    } catch (error) {
                      console.error('Error setting watch status:', error);
                    }
                  }
                  setForceShowPrivateSection(true);
                  setPrivateExpanded(true);
                  setIsEditingPrivate(true);
                }}
              >
                <div className="flex items-center gap-2">
                  <Plus size={16} className="text-black" />
                  <span className="text-sm font-bold text-black">Add private info</span>
                </div>
                <img src="/icons/filled_lock_icon.svg" alt="" className="w-4 h-4" />
              </button>
            ) : !privateExpanded ? (
              /* Collapsed (with data) - Title bar only */
              <button
                className="w-full h-[75px] px-4 flex items-center gap-3 text-left"
                onClick={() => setPrivateExpanded(true)}
              >
                <img src="/icons/filled_lock_icon.svg" alt="" className="w-6 h-6 shrink-0" />
                <div className="flex-1 min-w-0">
                  <h2 className="text-lg font-bold text-[#233454] leading-tight">Your private scouting notes</h2>
                  <p className="text-xs italic text-[#233454]">only you can see this</p>
                </div>
                <ChevronDown size={24} className="text-[#233454] shrink-0" />
              </button>
            ) : (
              /* Expanded View/Edit Mode */
              <div className="px-4 pt-4 pb-2">
                {/* Header Row - Lock + Title/Subtitle + Edit/Check */}
                <div className="flex items-start gap-2 mb-4">
                  <img src="/icons/filled_lock_icon.svg" alt="" className="w-6 h-6 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0 text-left">
                    <h2 className="text-lg font-bold text-[#233454] leading-tight">Your private scouting notes</h2>
                    <p className="text-xs italic text-[#233454]">only you can see this</p>
                  </div>
                  <button
                    className="p-1 hover:bg-white hover:bg-opacity-30 rounded transition-colors shrink-0"
                    aria-label={isEditingPrivate ? "Save changes" : "Edit"}
                    title={isEditingPrivate ? "Save" : "Edit"}
                    onClick={async () => {
                      if (isEditingPrivate) {
                        // Exiting edit mode - refresh data and reset forceShow
                        await recheckPrivateData();
                        setForceShowPrivateSection(false);
                      }
                      setIsEditingPrivate(!isEditingPrivate);
                    }}
                  >
                    {isEditingPrivate ? (
                      <Check size={24} className="text-[#233454]" />
                    ) : (
                      <Edit2 size={24} className="text-[#233454]" />
                    )}
                  </button>
                </div>

                {/* Content */}
                <div className="space-y-4 pl-1 min-w-0">
                  {/* Contact Info */}
                  <HorseContactInfo 
                    horseId={horse.unified_horse_key} 
                    inCollection={inCollection}
                    isEditing={isEditingPrivate}
                    initialContactNotes={preloadedContactNotes}
                  />

                  {/* Price */}
                  <HorsePriceDisplay
                    horseId={horse.unified_horse_key}
                    onPricingChanged={recheckPrivateData}
                    isEditing={isEditingPrivate}
                    initialPricing={preloadedPricing}
                  />

                  {/* Traits */}
                  <HorseTraits 
                    horseId={horse.unified_horse_key} 
                    inCollection={inCollection}
                    isEditing={isEditingPrivate}
                    initialProperties={preloadedProperties}
                  />

                  {/* Notes Section */}
                  <HorseNotes
                    horseId={horse.unified_horse_key}
                    horseName={horse.unified_horse_name}
                    initialShowForm={showNotesForm}
                    isEditing={isEditingPrivate}
                    initialNotes={preloadedNotes}
                    onFormVisibilityChange={(isVisible) => {
                      setShowNotesForm(isVisible);
                      if (!isVisible) {
                        recheckPrivateData();
                      }
                    }}
                  />
                </div>

                {/* Collapse Chevron at Bottom Center */}
                <div className="flex justify-center pt-3 pb-1">
                  <button
                    className="p-1 hover:bg-white hover:bg-opacity-30 rounded transition-colors"
                    aria-label="Collapse private section"
                    title="Collapse"
                    onClick={async () => {
                      setPrivateExpanded(false);
                      setIsEditingPrivate(false);
                      // Re-check private data and reset forceShow if no data exists
                      await recheckPrivateData();
                      // Reset forceShowPrivateSection - hasPrivateData will determine the correct mode
                      setForceShowPrivateSection(false);
                    }}
                  >
                    <ChevronUp size={24} className="text-[#233454]" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Public performance section (fixture/API gated) */}
        <HorsePerformanceSection
          horseId={horse.unified_horse_key}
          horseName={horse.unified_horse_name}
          horse={horse}
          onMaxLevelChange={setMaxShowHeight}
        />

      </div>
      {isPrivateHorse && (
        <LinkPrivateHorseModal
          isOpen={showLinkModal}
          privateHorseId={horseId}
          onClose={() => setShowLinkModal(false)}
          onLinked={handleLinkSuccess}
        />
      )}

      <ProfileSelectionPopup
        isOpen={profileAssignmentPending !== null}
        horseId={profileAssignmentPending?.horseId ?? ''}
        profiles={profiles}
        onConfirm={(selectedIds) => {
          if (selectedIds.length > 0) {
            setProfiles(prev => prev.map(p =>
              selectedIds.includes(p.id) ? { ...p, horse_count: p.horse_count + 1 } : p
            ));
          }
          setProfileAssignmentPending(null);
        }}
        onClose={() => setProfileAssignmentPending(null)}
        onProfileCreated={(newProfile: ProfileRead) => {
          setProfiles(prev => [...prev, { ...newProfile, horse_count: 0 }]);
        }}
      />
    </div>
  );
};
